import { Form, message } from "antd";
import { useEventTemplateCreateMutation } from "@ticketingplatform/api/dist/recurringEvent/hooks";
import { useIntl } from "react-intl";
import { useCallback, useMemo } from "react";
import {
  EventStatus,
  EventType,
  RecurringSettingsSeparator,
} from "@ticketingplatform/api/dist/__generated__/globalTypes";
import { useNavigate, useParams } from "react-router-dom";
import useImageUpload from "hooks/useImageUpload";
import { GET_EVENT_TEMPLATES } from "@ticketingplatform/api/dist/recurringEvent/queries";
import { getEnumValueByStringValue } from "utils/types";
import { ROUTE_PATHS } from "const/paths";
import { useVenueContext } from "contexts/VenueContext";
import { useDiscountCodesQuery } from "@ticketingplatform/api/dist/discountCode";
import { DeepPartial } from "setup/deepPartial";
import useApolloErrorFields from "hooks/useApolloErrorFields";
import usePrompt from "hooks/usePrompt";
import { pickTicketPoolFormFields } from "views/TicketTemplates/TicketTemplateCreate/utils";

import {
  EventTemplateFormProps,
  EventTemplateFormValues,
} from "../RecuringEventForm/types";
import { RecurringMode } from "../RecuringEventForm/Tabs/EventInfo/RecurringSettingsField/partials/RecurringSettingsForm/enums";
import { EVENT_INIT_VALUES } from "../RecuringEventForm/consts";

export const useRecuringEventForm = (): EventTemplateFormProps => {
  const { venue } = useVenueContext();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { type } = useParams();
  const eventType =
    getEnumValueByStringValue(EventType, type?.toUpperCase()) ||
    EventType.TICKETED;
  const intl = useIntl();
  const [createEventTemplate, { loading, error }] =
    useEventTemplateCreateMutation({
      evictQueries: [GET_EVENT_TEMPLATES],
    });
  const fields = useApolloErrorFields(error, true);
  const { uploadImage, isUploadLoading } = useImageUpload();
  const { data: discountData, loading: isDiscountLoading } =
    useDiscountCodesQuery(
      useMemo(
        () =>
          venue?.id
            ? {
                variables: {
                  filters: {
                    venueId: {
                      equals: venue.id,
                    },
                    isApplicableToAllEvents: {
                      equals: true,
                    },
                    OR: [
                      {
                        expireAt: null,
                      },
                      {
                        expireAt: {
                          gt: new Date(),
                        },
                      },
                    ],
                  },
                },
              }
            : {
                skip: true,
              },
        [venue?.id]
      )
    );

  usePrompt(loading || isUploadLoading);

  const initialValues = useMemo<DeepPartial<EventTemplateFormValues>>(
    () => ({
      ...EVENT_INIT_VALUES,
      input: {
        ...EVENT_INIT_VALUES.input,
        timezone: venue?.timezone,
        discountCodes: discountData?.discountCodes?.nodes || [],
      },
    }),
    [discountData?.discountCodes?.nodes, venue?.timezone]
  );

  const onFinish = useCallback(
    async (values: EventTemplateFormValues) => {
      const {
        input: {
          image,
          seatMap,
          ticketPools,
          discountCodes,
          recurringSettings,
          status,
          ...input
        },
      } = values;

      try {
        const imageId = await (async () => {
          if (!image) {
            return initialValues?.input?.image ? null : undefined;
          }

          if ("id" in image) {
            return undefined;
          }

          const {
            image: { id },
          } = await uploadImage(image);

          return id;
        })();

        const { data } = await createEventTemplate({
          variables: {
            input: {
              ...input,
              discountCodeIds: discountCodes?.map(({ id }) => id),
              venueId: venue?.id,
              ticketPools: ticketPools
                ? ticketPools.map(({ id: _, ...ticketPool }) =>
                    pickTicketPoolFormFields(ticketPool)
                  )
                : undefined,
              recurringSettings: (() => {
                if (!recurringSettings) {
                  return null;
                }

                const {
                  separator,
                  separatorCount,
                  mode,
                  endAt,
                  monthRepeatMode,
                  maxOccurrences,
                  ...rest
                } = recurringSettings;
                const baseRecurringSettings = {
                  separator,
                  separatorCount,
                  endAt: mode === RecurringMode.END_AT ? endAt : null,
                  maxOccurrences:
                    mode === RecurringMode.MAX_OCCURRENCES
                      ? maxOccurrences
                      : null,
                };

                switch (separator) {
                  case RecurringSettingsSeparator.DAILY:
                  case RecurringSettingsSeparator.YEARLY:
                    return baseRecurringSettings;
                  case RecurringSettingsSeparator.MONTHLY: {
                    return {
                      ...baseRecurringSettings,
                      monthRepeatMode,
                    };
                  }
                  case RecurringSettingsSeparator.WEEKLY: {
                    return {
                      ...baseRecurringSettings,
                      ...rest,
                    };
                  }
                  default:
                    return null;
                }
              })(),
              type: eventType,
              status: status || EventStatus.DRAFT,
              seatMapId: seatMap?.id,
              imageId,
            },
          },
        });

        if (!data) {
          return;
        }

        message.success(
          intl.formatMessage({
            defaultMessage: "Event template has been added!",
          })
        );

        navigate(ROUTE_PATHS.RecuringEventList, {
          state: {
            skipBlock: true,
          },
        });
      } catch {
        message.error(
          intl.formatMessage({
            defaultMessage: "Cannot create event template!",
          })
        );
      }
    },
    [
      createEventTemplate,
      eventType,
      initialValues?.input?.image,
      intl,
      navigate,
      uploadImage,
      venue?.id,
    ]
  );

  return {
    eventType,
    loading: loading || isUploadLoading || isDiscountLoading,
    form,
    fields,
    initialValues,
    onFinish,
  };
};
